'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import UILink from 'UIComponents/link/UILink';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UITag from 'UIComponents/tag/UITag';
import FilterTag from '../FilterTag';
import * as Category from 'ecosystem-marketplace-core/constants/groupTags/Category';
import { getCategoryI18nKey, getCategoryFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { getInstallsBucket } from 'ecosystem-marketplace-core/utils/offerings';
import { reportMissingCategoryFeatureTranslation } from '../../../../utils/sentry';
import { getFormattedUrl } from '../../../../utils/urls';
import { CATEGORY_FEATURES } from '../../../../constants/search/FilterKey';
const I18N_PATH = 'appMarketplace.detail.overview.sidebar.details';

const InstallsBucket = ({
  value
}) => {
  const bucket = getInstallsBucket(value);

  if (!bucket) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${I18N_PATH}.installsBuckets.min`
    });
  }

  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `${I18N_PATH}.installsBuckets.bucket_jsx`,
    options: {
      value: bucket
    },
    elements: {
      Number: FormattedNumber
    }
  });
};

const Section = ({
  children,
  label,
  labelClassName
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    className: labelClassName,
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});

const AppDetails = ({
  appLanguages,
  category,
  companyName,
  companyUrl,
  categoryFeatures,
  getFilterLink,
  homemade,
  installs,
  isPreview,
  onTrackInteraction
}) => {
  const trackInteraction = useCallback((action, value) => onTrackInteraction(action, 'details', value), [onTrackInteraction]);
  const renderedCategorySection = useMemo(() => {
    return category ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.categoryLabel`),
      labelClassName: "m-bottom-1",
      children: /*#__PURE__*/_jsx(UIRouterLink, {
        disabled: isPreview,
        to: getFilterLink(),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getCategoryI18nKey(category)
            })
          })
        })
      })
    }) : null;
  }, [category, getFilterLink, isPreview]);
  const renderedFeaturesSection = useMemo(() => {
    return category && !categoryFeatures.isEmpty() ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.featuresLabel`),
      labelClassName: "m-bottom-1",
      children: categoryFeatures.map(feature => [feature, getCategoryFeatureI18nKey(category, feature)]).filter(([feature, featureI18nKey]) => {
        if (!isValidI18nKey(featureI18nKey)) {
          reportMissingCategoryFeatureTranslation(category, feature, {
            component: 'AppDetail'
          });
          return false;
        }

        return true;
      }).sortBy(([__, featureI18nKey]) => unescapedText(featureI18nKey)).map(([feature, featureI18nKey]) => /*#__PURE__*/_jsx(UIRouterLink, {
        disabled: isPreview,
        to: getFilterLink(CATEGORY_FEATURES, feature),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            className: "m-left-0 m-right-1",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: featureI18nKey
            })
          })
        })
      }, feature))
    }) : null;
  }, [category, categoryFeatures, getFilterLink, isPreview]);
  const renderedLanguagesSection = useMemo(() => {
    if (appLanguages.size === 0 || appLanguages.some(lang => !lang)) {
      return null;
    }

    return /*#__PURE__*/_jsx(Section, {
      label: homemade ? unescapedText(`${I18N_PATH}.homemadeAppLanguagesLabel`) : unescapedText(`${I18N_PATH}.appLanguagesLabel`),
      labelClassName: "m-bottom-1",
      children: /*#__PURE__*/_jsx(FormattedList, {
        children: appLanguages.map(languageCode => [languageCode, `SharedI18nStrings.languageNames.${languageCode}`]).sort(([__, languageNameI18nKey]) => unescapedText(languageNameI18nKey)).map(([languageCode, languageNameI18nKey]) => /*#__PURE__*/_jsx(FormattedMessage, {
          message: languageNameI18nKey
        }, languageCode))
      })
    });
  }, [appLanguages, homemade]);
  const renderedCompanyName = useMemo(() => {
    return companyUrl ? /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: getFormattedUrl(companyUrl),
      onClick: () => trackInteraction('click provider link'),
      children: companyName
    }) : companyName;
  }, [companyName, companyUrl, trackInteraction]);
  const renderedInstallsSection = useMemo(() => {
    return /*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.installsLabel`),
      children: /*#__PURE__*/_jsx(InstallsBucket, {
        value: installs
      })
    });
  }, [installs]);
  return /*#__PURE__*/_jsxs(UIDescriptionList, {
    children: [/*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.providerLabel`),
      children: renderedCompanyName
    }), renderedInstallsSection, renderedCategorySection, renderedFeaturesSection, renderedLanguagesSection]
  });
};

AppDetails.propTypes = {
  appLanguages: ImmutablePropTypes.setOf(PropTypes.string).isRequired,
  category: PropTypes.oneOf(Object.values(Category)),
  categoryFeatures: ImmutablePropTypes.set.isRequired,
  companyName: PropTypes.string.isRequired,
  companyUrl: PropTypes.string,
  getFilterLink: PropTypes.func.isRequired,
  homemade: PropTypes.bool.isRequired,
  installs: PropTypes.number.isRequired,
  isPreview: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired
};
export default AppDetails;